<template>
    <button
        class="icon-button c-hover"
        type="button"
        :class="[`icon-button-${type}`, disabled ? 'icon-button-disabled-' + disabledType : '', { 'icon-button-loading': loading }]"
        :style="{ width: `${pxToRem(props.size)}`, height: `${pxToRem(props.size)}` }"
        :disabled="disabled || loading"
        @click="handleClick">
        <span
            v-if="loading"
            class="icon-button-loading">
            <i class="loading-icon"></i>
        </span>
        <span class="icon-button-content">
            <nicon
                :icon="icon"
                :size="iconSize" />
        </span>
        <span
            v-if="iconBadge"
            class="icon-button-badge">
            {{ iconBadge }}
        </span>
    </button>
</template>

<script setup lang="ts">
import { defineEmits } from 'vue';
import { pxToRem } from '@/utils/rem/rem';

interface Props {
    size?: number;
    iconSize?: number;
    type?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'gray' | 'transparent';
    disabled?: boolean;
    loading?: boolean;
    icon?: string;
    iconBadge?: number | string;
    disabledType?: 'opacity' | 'normal';
}

const props = withDefaults(defineProps<Props>(), {
    size: 40,
    iconSize: 20,
    mode: 'button',
    type: 'primary',
    disabled: false,
    loading: false,
    icon: '',
    iconBadge: 0,
    disabledType: 'normal'
});

const emit = defineEmits<{
    click: [event: MouseEvent];
}>();

const handleClick = (event: MouseEvent) => {
    if (!props.disabled && !props.loading) {
        emit('click', event);
    }
};
</script>

<style scoped lang="scss">
.icon-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    outline: none;
    user-select: none;
    white-space: nowrap;
    line-height: 1;
    border-radius: 50%;
    padding: 0;

    &:focus {
        outline: none;
    }

    .icon-button-badge {
        position: absolute;
        top: 0;
        left: 36px;
        height: 20px;
        border-radius: 10px;
        font-size: 15px;
        line-height: 20px;
        background-color: var(--color-red);
        color: #fff;
        padding: 0 5px;
        display: flex;
        align-content: center;
        justify-content: center;
        min-width: 40px;
        box-sizing: border-box;
    }

    // 尺寸样式 ，不要了，使用size属性，设计稿尺寸太乱了
    // &-small {
    //     width: var(--icon-button-small-height);
    //     height: var(--icon-button-small-height);
    // }

    // &-medium {
    //     width: var(--icon-button-medium-height);
    //     height: var(--icon-button-medium-height);
    // }

    // &-large {
    //     width: var(--icon-button-large-height);
    //     height: var(--icon-button-large-height);
    // }

    // &-crazy {
    //     width: var(--icon-button-crazy-height);
    //     height: var(--icon-button-crazy-height);
    // }

    // 类型样式
    &-primary {
        background: var(--icon-button-primary-bg);
        color: var(--icon-button-primary-color);

        &:hover:not(.icon-button-disabled-normal):not(.icon-button-disabled-opacity):not(.icon-button-loading) {
            opacity: 0.8;
        }
    }

    &-base {
        background: var(--color-base);
        color: #fff;

        &:hover:not(.icon-button-disabled-normal):not(.icon-button-disabled-opacity):not(.icon-button--loading) {
            opacity: 0.8;
        }
    }

    &-transparent {
        background: transparent;
        color: var(--icon-button-transparent-color);

        &:hover:not(.icon-button-disabled-normal):not(.icon-button-disabled-opacity):not(.icon-button--loading) {
            opacity: 0.8;
        }
    }

    &-gray {
        background: var(--icon-button-gray-bg);
        color: var(--icon-button-gray-color);

        &:hover:not(.icon-button-disabled-normal):not(.icon-button-disabled-opacity):not(.icon-button--loading) {
            opacity: 0.8;
        }
    }

    // 禁用状态
    &-disabled-normal {
        background: var(--icon-button-disabled-bg);
        color: var(--icon-button-disabled-color);
        cursor: not-allowed;
        transform: none !important;
        box-shadow: none !important;
    }

    &-disabled-opacity {
        opacity: 0.5;
        cursor: not-allowed;
    }

    // 加载状态
    &-loading {
        background: var(--icon-button-disabled-bg);
        color: var(--icon-button-disabled-color);
        cursor: not-allowed;
    }
}
</style>
